import "./PropertyAccess.scss";
import { useCallback, useContext } from "react";
import { LoadScript } from "@react-google-maps/api";
import { AppContext } from "AppContext";
import {
  ACCESS_TYPE_CONCIERGE,
  ACCESS_TYPE_KEYLESS_DIGITAL_LOCK,
  ACCESS_TYPE_KEYNEST,
  ACCESS_TYPE_NEARBY_MERCHANT,
  ACCESS_TYPE_RAILING_LOCKBOX,
  ACCESS_TYPE_SALTO,
  ACCESS_TYPE_WALL_MOUNTED_LOCKBOX,
  ACCESS_TYPE_WELCOME_WIZARD
} from "data/accessTypes";
import config from "config";
import { InfoContainer } from "components/InfoContainer";
import GoogleStore from "assets/badge_google_store.png";
import AppleStore from "assets/badge_app_store.png";
import { Map } from "components/Map";
import { KeystoreInformation } from "components/KeystoreInformation";

const APP_STORE_URL =
  "https://apps.apple.com/us/app/justin-mobile/id960998088";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.saltosystems.justin";

const SaltoInstructions = () => {
  const {
    booking: { property }
  } = useContext(AppContext);

  const location = property?.home_info?.location;

  return (
    <>
      <p>
        The apartment has a keyless lock which can be opened
        via the “JustIn” mobile app. Please download the app
        on your smartphone. Let us know once this is done
        and please send us the mobile number that is
        associated to this device.
      </p>

      {location?.access_map && (
        <p>
          <strong>
            Please find a detailed guide with photos for
            accessing the property{" "}
            <a
              href={location.access_map}
              target="_blank"
              rel="noreferrer"
            >
              here.
            </a>
          </strong>
        </p>
      )}

      <p>{location?.access_type_notes}</p>

      <p>
        <strong>
          Please note, if we don&apos;t receive the mobile
          numbers for all guests that have been registered,
          we won&apos;t be able to approve your device to
          access the property. Thank you!
        </strong>
      </p>
      <div className="PropertyAccess__links">
        <a
          className="PropertyAccess__link"
          target="_blank"
          rel="noopener noreferrer"
          href={PLAY_STORE_URL}
        >
          <img
            alt="Download from apple app store"
            src={GoogleStore}
            className="PropertyAccess__link-image"
          />
        </a>
        <a
          className="PropertyAccess__link"
          target="_blank"
          rel="noopener noreferrer"
          href={APP_STORE_URL}
        >
          <img
            alt="Download from google store"
            src={AppleStore}
            className="PropertyAccess__link-image"
          />
        </a>
      </div>
    </>
  );
};

const LockboxInstructions = () => {
  const {
    booking: { property }
  } = useContext(AppContext);

  const location = property?.home_info?.location;
  const accessType =
    property?.home_info?.location?.access_type;
  const lockboxCode =
    accessType === ACCESS_TYPE_KEYLESS_DIGITAL_LOCK
      ? location?.digital_lock_code
      : location?.lockbox_code;

  return (
    <>
      <p>
        Access to the home can be obtained from a{" "}
        {location?.access_type}
      </p>

      {location?.access_map && (
        <p>
          <strong>
            Please find a detailed guide with photos for
            accessing the property{" "}
            <a
              href={location.access_map}
              target="_blank"
              rel="noreferrer"
            >
              here.
            </a>
          </strong>
        </p>
      )}

      {lockboxCode ? (
        <p>
          The code to the {location?.access_type} is{" "}
          <strong>{lockboxCode}</strong>.{" "}
          {accessType === ACCESS_TYPE_RAILING_LOCKBOX ||
          accessType === ACCESS_TYPE_WALL_MOUNTED_LOCKBOX
            ? "Please do not leave any codes visible after you collect your keys and please ensure to return the keys to the lockbox at checkout."
            : "Do not leave any codes visible."}
        </p>
      ) : (
        <p>
          You will receive your lockbox code 48 hours before
          your arrival date.
        </p>
      )}

      <p>{location?.access_type_notes}</p>
    </>
  );
};

const KeynestInstructions = () => {
  const {
    booking: {
      contact_number: contactNumber,
      guest_key: guestKey,
      property
    }
  } = useContext(AppContext);

  const location = property?.home_info?.location;

  return (
    <>
      <p>
        Access to the home can be obtained from a keystore{" "}
        {guestKey?.keystore_location.name} located at{" "}
        {guestKey?.keystore_location.street_address}. Go to
        the counter at the keynest location, mention you are
        picking up keys, and provide the code
        {guestKey?.collection_code ? (
          <> - {guestKey?.collection_code}.</>
        ) : (
          "."
        )}
      </p>

      {!guestKey?.collection_code && (
        <p>
          You will receive your keynest code 48 hours before
          your arrival date.
        </p>
      )}

      <p>{location?.access_type_notes}</p>

      <KeystoreInformation
        contactNumber={contactNumber}
        guestKey={guestKey}
        location={location}
      />
    </>
  );
};

const WelcomeWizardInstructions = () => {
  const {
    booking: { property }
  } = useContext(AppContext);

  const location = property?.home_info?.location;

  return (
    <>
      <p>
        One of our check-in agents will meet you at the
        apartment at your chosen check-in time.
      </p>

      <p>{location?.access_type_notes}</p>
    </>
  );
};

const NearbyMerchantInstructions = () => {
  const {
    booking: { property }
  } = useContext(AppContext);

  const location = property?.home_info?.location;

  return (
    <>
      <p>
        Access to the home can be obtained from a{" "}
        {location?.access_type}
      </p>

      {location?.access_map && (
        <p>
          <strong>
            Please find a detailed guide with photos for
            accessing the property{" "}
            <a
              href={location.access_map}
              target="_blank"
              rel="noreferrer"
            >
              here.
            </a>
          </strong>
        </p>
      )}

      {location?.lockbox_code ? (
        <p>
          The code to the {location?.access_type} is{" "}
          {location?.lockbox_code}. Do not leave any codes
          visible.
        </p>
      ) : (
        <p>
          You will receive your code 48 hours before your
          arrival date.
        </p>
      )}

      <p>{location?.access_type_notes}</p>
    </>
  );
};

const ConciergeInstructions = () => {
  const {
    booking: { property }
  } = useContext(AppContext);

  const location = property?.home_info?.location;

  return (
    <>
      <p>
        Access to the home can be obtained from a{" "}
        {location?.access_type}.
      </p>
      <p>{location?.access_type_notes}</p>
    </>
  );
};

const getInstructions = accessType => {
  switch (accessType) {
    case ACCESS_TYPE_WALL_MOUNTED_LOCKBOX:
    case ACCESS_TYPE_RAILING_LOCKBOX:
    case ACCESS_TYPE_KEYLESS_DIGITAL_LOCK:
      return <LockboxInstructions />;
    case ACCESS_TYPE_KEYNEST:
      return <KeynestInstructions />;
    case ACCESS_TYPE_CONCIERGE:
      return <ConciergeInstructions />;
    case ACCESS_TYPE_WELCOME_WIZARD:
      return <WelcomeWizardInstructions />;
    case ACCESS_TYPE_NEARBY_MERCHANT:
      return <NearbyMerchantInstructions />;
    case ACCESS_TYPE_SALTO:
      return <SaltoInstructions />;
    default:
      return null;
  }
};

const isLockbox = accessType =>
  [
    ACCESS_TYPE_RAILING_LOCKBOX,
    ACCESS_TYPE_WALL_MOUNTED_LOCKBOX
  ].includes(accessType);

const isKeynest = accessType =>
  accessType === ACCESS_TYPE_KEYNEST;

export const PropertyAccess = () => {
  const {
    booking: {
      guest_key: guestKey,
      property,
      property_access_instructions_available:
        propertyAccessInstructionsAvailable
    }
  } = useContext(AppContext);

  const propertyLocationNotes =
    property?.home_info?.location?.property_location_notes;
  const accessType =
    property?.home_info?.location?.access_type;

  const lat = property?.home_info?.location.property_lat;
  const lng = property?.home_info?.location.property_lng;

  const getAccessCoordinates = useCallback(() => {
    if (isLockbox(accessType)) {
      return {
        lat: property?.home_info?.location.lockbox_lat,
        lng: property?.home_info?.location.lockbox_lng
      };
    }
    if (isKeynest) {
      return {
        lat: guestKey?.keystore_location?.lat,
        lng: guestKey?.keystore_location?.lng
      };
    }
    return {
      lat: null,
      lng: null
    };
  }, [accessType, guestKey, property]);

  const accessCenter = getAccessCoordinates();

  if (!propertyAccessInstructionsAvailable) {
    return (
      <section className="PropertyAccess">
        <LoadScript
          googleMapsApiKey={config.GOOGLE_API_KEY}
        >
          <Map lat={lat} lng={lng} />
        </LoadScript>
        <InfoContainer>
          <p>
            Information on how to enter the property will be
            displayed here 48 hours before check-in date.
          </p>
        </InfoContainer>
      </section>
    );
  }

  return (
    <section className="PropertyAccess">
      <LoadScript googleMapsApiKey={config.GOOGLE_API_KEY}>
        <h2 className="header">Getting there</h2>

        <ol className="PropertyAccess__list">
          <li className="PropertyAccess__item big-text">
            Collect the keys
            <div
              className={
                accessCenter.lat && accessCenter.lng
                  ? "PropertyAccess__grid"
                  : ""
              }
            >
              <div className="PropertyAccess__description">
                {getInstructions(accessType)}
              </div>

              <Map
                lat={accessCenter.lat}
                lng={accessCenter.lng}
              />
            </div>
          </li>

          <li className="PropertyAccess__item big-text">
            Find the property
            <div
              className={
                propertyLocationNotes
                  ? "PropertyAccess__grid"
                  : "PropertyAccess__wrapper"
              }
            >
              {propertyLocationNotes && (
                <div className="PropertyAccess__description">
                  {propertyLocationNotes}
                </div>
              )}

              <Map lat={lat} lng={lng} />
            </div>
          </li>

          <li className="PropertyAccess__item big-text">
            Enjoy your stay!
            {accessType !==
              ACCESS_TYPE_KEYLESS_DIGITAL_LOCK && (
              <div className="PropertyAccess__description">
                <p>
                  Please be sure to bring keys with you each
                  time you leave the property and take care
                  not to lose any keys during your stay. A
                  callout fee will be charged to provide
                  access to the property during your stay or
                  if keys are not returned at checkout to
                  the place you picked them up. Thank you in
                  advance for your cooperation and
                  understanding!
                </p>
              </div>
            )}
          </li>
        </ol>
      </LoadScript>
    </section>
  );
};
